import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_ORDER = [1, 2, 3, 4];
const PHASE_NAMES = { 1: 'Stabilize', 2: 'Understand', 3: 'Reclaim', 4: 'Renew' };

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

const palette = (variant) =>
  variant === 'dark'
    ? {
        title: '#1A1A2E',
        text: '#4A4A5A',
        dim: '#8A8798',
        line: '#E5DFD6',
        cardBg: '#F9F4F0',
        accent: '#C9A84C',
      }
    : {
        title: '#44403c',
        text: '#57534e',
        dim: '#a8a29e',
        line: '#e7e5e4',
        cardBg: '#4A4A5A',
        accent: '#b08d57',
      };

export const PhaseJourneyTimeline = ({ variant = 'dark' }) => {
  const [timeline, setTimeline] = useState(null);
  const c = palette(variant);

  useEffect(() => {
    axios
      .get(`${API}/phase/journey`, { withCredentials: true })
      .then((res) => setTimeline(res.data.timeline || []))
      .catch((err) => logError('Phase journey fetch failed:', err?.response?.status));
  }, []);

  if (!timeline) return null;

  const byPhase = Object.fromEntries(timeline.map((t) => [t.phase, t]));
  const currentPhase = timeline.find((t) => t.current)?.phase || 1;

  return (
    <div className="text-left" data-testid="phase-journey-timeline">
      <div className="space-y-0">
        {PHASE_ORDER.map((num, i) => {
          const entry = byPhase[num];
          const isCurrent = num === currentPhase;
          const isPast = entry && !isCurrent;
          const isAhead = !entry && num > currentPhase;
          const date = formatDate(entry?.assigned_at);

          return (
            <div key={num} className="flex gap-4" data-testid={`phase-journey-step-${num}`}>
              {/* Marker column */}
              <div className="flex flex-col items-center">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
                  style={{
                    background: isCurrent
                      ? c.accent
                      : isPast
                      ? `${c.accent}66`
                      : 'transparent',
                    border: isAhead ? `1.5px dashed ${c.line}` : `1.5px solid ${isCurrent ? c.accent : `${c.accent}66`}`,
                    boxShadow: isCurrent ? `0 0 12px ${c.accent}55` : 'none',
                  }}
                />
                {i < PHASE_ORDER.length - 1 && (
                  <div className="w-px flex-1 my-1" style={{ background: c.line, minHeight: 28 }} />
                )}
              </div>

              {/* Content */}
              <div className="pb-6">
                <p
                  className="text-sm"
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    color: isAhead ? c.dim : c.title,
                  }}
                >
                  Phase {num} — {entry?.phase_name || PHASE_NAMES[num]}
                  {isCurrent && (
                    <span
                      className="ml-2 px-2.5 py-0.5 rounded-full text-[10px] tracking-[0.1em] align-middle"
                      style={{ background: `${c.accent}22`, color: c.accent, fontFamily: 'Jost, sans-serif' }}
                      data-testid="phase-journey-current-badge"
                    >
                      YOU ARE HERE
                    </span>
                  )}
                </p>
                <p className="text-xs mt-1" style={{ fontFamily: 'Jost, sans-serif', color: c.dim }}>
                  {isAhead
                    ? 'Ahead of you, whenever you arrive'
                    : date
                    ? `${isPast ? 'Began' : 'Since'} ${date}`
                    : isCurrent
                    ? 'Where you are now'
                    : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
